import { inject, ref } from 'vue'

const account = ref(null)
const loading = ref(false)
const error = ref('')
let profileRequest = null
let profileAccessToken = ''

export function ensureStudioProfile(api) {
  const accessToken = api.tokenStore.getAccessToken()
  if (!accessToken) throw new Error('Studio access token is required')
  if (account.value && profileAccessToken === accessToken) return Promise.resolve(account.value)
  if (profileRequest?.accessToken === accessToken) return profileRequest.promise

  const promise = api.account.getProfile()
    .then(profile => {
      if (api.tokenStore.getAccessToken() === accessToken) {
        account.value = profile
        profileAccessToken = accessToken
      }
      return profile
    })
    .finally(() => {
      if (profileRequest?.promise === promise) profileRequest = null
    })
  profileRequest = { accessToken, promise }
  return promise
}

function clearProfile() {
  profileRequest = null
  profileAccessToken = ''
  account.value = null
}

export function useStudioAuth() {
  const api = inject('api')
  if (!api) throw new Error('useStudioAuth: api provider is missing')

  async function loadProfile() {
    if (!api.tokenStore.getAccessToken()) {
      if (!api.tokenStore.getRefreshToken()) {
        account.value = null
        return null
      }
      try {
        await api.tokenStore.refresh()
      } catch (err) {
        error.value = err?.message || 'REFRESH_FAILED'
        account.value = null
        return null
      }
    }
    loading.value = true
    error.value = ''
    try {
      account.value = await ensureStudioProfile(api)
      return account.value
    } catch (err) {
      error.value = err?.message || 'PROFILE_FAILED'
      clearProfile()
      return null
    } finally {
      loading.value = false
    }
  }

  async function login({ email, password }) {
    loading.value = true
    error.value = ''
    try {
      const session = await api.auth.login({ email, password })
      api.tokenStore.setTokens(session)
      account.value = session.account
      return session.account
    } catch (err) {
      error.value = err?.message || 'LOGIN_FAILED'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    const refreshToken = api.tokenStore.getRefreshToken()
    try {
      if (refreshToken) await api.auth.logout({ refresh_token: refreshToken })
    } finally {
      api.tokenStore.clear()
      clearProfile()
    }
  }

  return { account, loading, error, loadProfile, login, logout }
}
